import {
  Accordion,
  Alert,
  Badge,
  List,
  Stack,
  Text,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconChevronDown,
  IconInfoCircle,
  IconUrgent,
} from '@tabler/icons-react';
import type { ProtocolSection } from '../data/mockSessionData';

interface ProtocolsTabProps {
  protocols: ProtocolSection[];
}

function categoryColor(cat: ProtocolSection['category']): string {
  switch (cat) {
    case 'risk-assessment': return 'red';
    case 'crisis-management': return 'orange';
    case 'safety-planning': return 'blue';
    case 'clinical-protocol': return 'gray';
  }
}

function categoryLabel(cat: ProtocolSection['category']): string {
  switch (cat) {
    case 'risk-assessment': return 'Risk';
    case 'crisis-management': return 'Crisis';
    case 'safety-planning': return 'Safety';
    case 'clinical-protocol': return 'Protocol';
  }
}

function alertIcon(severity: 'info' | 'warning' | 'critical') {
  switch (severity) {
    case 'critical': return <IconUrgent size={16} />;
    case 'warning': return <IconAlertTriangle size={16} />;
    default: return <IconInfoCircle size={16} />;
  }
}

function alertColor(severity: 'info' | 'warning' | 'critical'): string {
  switch (severity) {
    case 'critical': return 'red';
    case 'warning': return 'yellow';
    default: return 'blue';
  }
}

export function ProtocolsTab({ protocols }: ProtocolsTabProps): JSX.Element {
  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed" lh={1.5}>
        Quick-reference help and clinical protocols for in-session use.
      </Text>

      <Accordion
        variant="separated"
        radius="sm"
        multiple
        chevron={<IconChevronDown size={16} />}
        styles={{
          item: {
            border: '1px solid var(--mantine-color-gray-3)',
            borderRadius: 'var(--mantine-radius-sm)',
            backgroundColor: 'white',
          },
          control: {
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 12,
            paddingRight: 12,
          },
          label: {
            width: '100%',
          },
        }}
      >
        {protocols.map((proto) => (
          <Accordion.Item key={proto.id} value={proto.id}>
            <Accordion.Control>
              <Stack gap={6}>
                <Badge size="xs" variant="light" color={categoryColor(proto.category)} w="fit-content">
                  {categoryLabel(proto.category)}
                </Badge>
                <Text size="sm" fw={600}>{proto.title}</Text>
              </Stack>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="sm">
                {proto.alerts?.map((alert, i) => (
                  <Alert
                    key={i}
                    color={alertColor(alert.severity)}
                    icon={alertIcon(alert.severity)}
                    variant="light"
                    py="xs"
                    radius="sm"
                  >
                    {/* eslint-disable-next-line no-restricted-syntax -- authored validation text from our own code, not a caught error */}
                    <Text size="xs">{alert.message}</Text>
                  </Alert>
                ))}

                <List type="ordered" size="sm" spacing="sm">
                  {proto.steps.map((step, i) => (
                    <List.Item key={i}>
                      <Stack gap={2}>
                        <Text size="sm" fw={600}>{step.label}</Text>
                        <Text size="xs" c="gray.7">{step.detail}</Text>
                      </Stack>
                    </List.Item>
                  ))}
                </List>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Stack>
  );
}
